import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Activity, TrendingUp, TrendingDown, Droplet, Utensils, Pill, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { glucoseAPI, mealsAPI, medicationsAPI, insightsAPI, analyticsAPI } from '../services/api';

const Dashboard = ({ user }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [glucoseLogs, setGlucoseLogs] = useState([]);
  const [meals, setMeals] = useState([]);
  const [medications, setMedications] = useState([]);
  const [insights, setInsights] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [glucoseData, mealsData, medsData, insightsData, analyticsData] = await Promise.all([
        glucoseAPI.getAll(10).catch(() => []),
        mealsAPI.getAll(10).catch(() => []),
        medicationsAPI.getAll().catch(() => []),
        insightsAPI.getAll(3).catch(() => []),
        analyticsAPI.getSummary().catch(() => null)
      ]);
      
      setGlucoseLogs(glucoseData);
      setMeals(mealsData);
      setMedications(medsData);
      setInsights(insightsData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate stats
  const latestGlucose = glucoseLogs.length > 0 ? glucoseLogs[0].glucose_value : 0;
  const avgGlucose = analytics?.average_glucose || 0;
  
  const todayMeals = meals.filter(meal => {
    const mealDate = new Date(meal.time);
    const today = new Date();
    return mealDate.toDateString() === today.toDateString();
  });
  const totalCarbs = todayMeals.reduce((sum, meal) => sum + meal.carbs, 0);
  const totalCalories = todayMeals.reduce((sum, meal) => sum + meal.calories, 0);

  const takenMeds = medications.filter(med => med.taken_today).length;
  const totalMeds = medications.length;
  const medAdherence = totalMeds > 0 ? Math.round((takenMeds / totalMeds) * 100) : 0;

  const statCards = [
    {
      title: t('dashboard.glucoseLevel'),
      value: latestGlucose ? `${latestGlucose} ${t('glucose.unit')}` : t('common.noData'),
      description: avgGlucose ? `${t('dashboard.average', 'Avg')}: ${avgGlucose} ${t('glucose.unit')}` : t('dashboard.startLogging', 'Start logging!'),
      icon: Droplet,
      trend: latestGlucose && latestGlucose <= (user?.target_glucose_max || 130) ? 'up' : 'down',
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
      iconColor: 'text-emerald-600 dark:text-emerald-400'
    },
    {
      title: t('dashboard.mealsLogged'),
      value: todayMeals.length || 0,
      description: todayMeals.length > 0 ? `${totalCarbs}g ${t('meals.carbs')}, ${totalCalories} ${t('meals.calories')}` : t('dashboard.logMeals', 'Log your meals'),
      icon: Utensils,
      trend: 'neutral',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400'
    },
    {
      title: t('dashboard.medicationsTaken'),
      value: totalMeds > 0 ? `${takenMeds}/${totalMeds}` : t('common.setup', 'Setup'),
      description: totalMeds > 0 ? `${medAdherence}% ${t('dashboard.adherence', 'adherence')}` : t('medications.addMedication'),
      icon: Pill,
      trend: medAdherence >= 90 ? 'up' : medAdherence > 0 ? 'down' : 'neutral',
      color: 'from-violet-500 to-purple-500',
      bgColor: 'bg-violet-50 dark:bg-violet-900/30',
      iconColor: 'text-violet-600 dark:text-violet-400'
    },
    {
      title: t('dashboard.inRange', 'In Range'),
      value: analytics?.in_range_percentage ? `${analytics.in_range_percentage}%` : 'N/A',
      description: t('dashboard.targetCompliance', 'Target range compliance'),
      icon: Activity,
      trend: (analytics?.in_range_percentage || 0) >= 70 ? 'up' : 'down',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50 dark:bg-orange-900/30',
      iconColor: 'text-orange-600 dark:text-orange-400'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-blue-500 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold mb-2">{t('dashboard.welcome')}, {user?.name?.split(' ')[0] || 'User'}!</h2>
            <p className="text-emerald-50 text-lg">{t('dashboard.healthSummary', "Here's your health summary for today")}</p>
          </div>
          <Button 
            onClick={fetchData} 
            variant="ghost" 
            size="icon"
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-0 shadow-md dark:bg-gray-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {stat.title}
                </CardTitle>
                <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</div>
                <div className="flex items-center space-x-2">
                  {stat.trend === 'up' && <TrendingUp className="w-4 h-4 text-emerald-600" />}
                  {stat.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-600" />}
                  <p className="text-sm text-gray-600 dark:text-gray-400">{stat.description}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Glucose Readings */}
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl dark:text-white">Recent Glucose Readings</CardTitle>
                <CardDescription className="dark:text-gray-400">Last measurements</CardDescription>
              </div>
              <Link to="/glucose">
                <Button variant="outline" size="sm" className="dark:border-gray-600 dark:text-gray-300">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {glucoseLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Droplet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No glucose readings yet</p>
                <Link to="/glucose">
                  <Button variant="link" className="mt-2">Add your first reading</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {glucoseLogs.slice(0, 5).map((log, index) => {
                  const targetMin = user?.target_glucose_min || 80;
                  const targetMax = user?.target_glucose_max || 130;
                  const isInRange = log.glucose_value >= targetMin && log.glucose_value <= targetMax;
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${isInRange ? 'bg-emerald-500' : 'bg-orange-500'}`} />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{log.glucose_value} mg/dL</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{log.meal_relation}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(log.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Insights */}
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl dark:text-white">AI Insights</CardTitle>
                <CardDescription className="dark:text-gray-400">Personalized recommendations</CardDescription>
              </div>
              <Link to="/insights">
                <Button variant="outline" size="sm" className="dark:border-gray-600 dark:text-gray-300">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {insights.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No insights yet</p>
                <p className="text-sm mt-1">Log more data to get AI insights</p>
              </div>
            ) : (
              <div className="space-y-3">
                {insights.map((insight, index) => {
                  const priorityColors = {
                    high: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
                    medium: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
                    low: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
                  };
                  return (
                    <div key={index} className={`p-4 rounded-lg border ${priorityColors[insight.priority] || priorityColors.medium} hover:shadow-md transition-shadow`}>
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium mb-1">{insight.category}</p>
                          <p className="text-sm">{insight.generated_text}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Today's Meals */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl dark:text-white">Today's Meals</CardTitle>
              <CardDescription className="dark:text-gray-400">Nutritional summary</CardDescription>
            </div>
            <Link to="/meals">
              <Button variant="outline" size="sm" className="dark:border-gray-600 dark:text-gray-300">View All</Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {todayMeals.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Utensils className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No meals logged today</p>
              <Link to="/meals">
                <Button variant="link" className="mt-2">Log your first meal</Button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {todayMeals.slice(0, 3).map((meal, index) => (
                <div key={index} className="p-4 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-lg border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{meal.meal_name}</h4>
                  <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                    <p>Carbs: <span className="font-medium text-gray-900 dark:text-white">{meal.carbs}g</span></p>
                    <p>Sugar: <span className="font-medium text-gray-900 dark:text-white">{meal.sugar}g</span></p>
                    <p>Calories: <span className="font-medium text-gray-900 dark:text-white">{meal.calories}</span></p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                      {new Date(meal.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
